import { sanitizeHtml } from '@/utils/html';

export const CHANGELOG_URL = 'https://docs.littlebird.ai/changelog/';
export const CHANGELOG_FEED_URL = 'https://docs.littlebird.ai/changelog.xml';

export type ChangelogEntry = {
  id: string;
  title: string;
  url: string;
  publishedAt: number;
};

export type ChangelogEntryPage = {
  imageUrl: string | null;
  description: string | null;
  contentHtml: string | null;
};

const DOCS_HOST = new URL(CHANGELOG_URL).hostname;

const toDocsUrl = (candidate: string, base?: string): string | null => {
  try {
    const url = new URL(candidate, base);
    return url.protocol === 'https:' && url.hostname === DOCS_HOST
      ? url.href
      : null;
  } catch {
    return null;
  }
};

const URL_ATTRIBUTES = ['href', 'src', 'poster'];

const toAbsoluteUrl = (candidate: string, base: string): string | null => {
  try {
    return new URL(candidate, base).href;
  } catch {
    return null;
  }
};

const toContentHtml = (prose: Element, pageUrl: string): string => {
  for (const editSurface of prose.querySelectorAll('[data-edit-surface]')) {
    editSurface.remove();
  }
  for (const element of prose.querySelectorAll('*')) {
    element.removeAttribute('class');
    element.removeAttribute('style');
    for (const name of URL_ATTRIBUTES) {
      const value = element.getAttribute(name);
      if (value === null) continue;
      const absoluteUrl = toAbsoluteUrl(value, pageUrl);
      if (absoluteUrl) element.setAttribute(name, absoluteUrl);
      else element.removeAttribute(name);
    }
  }
  return sanitizeHtml(prose.innerHTML);
};

export const parseChangelogFeed = (xml: string): ChangelogEntry[] => {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  return Array.from(doc.querySelectorAll('item')).flatMap((item) => {
    const title = item.querySelector('title')?.textContent?.trim();
    const url = toDocsUrl(
      item.querySelector('link')?.textContent?.trim() ?? ''
    );
    const publishedAt = Date.parse(
      item.querySelector('pubDate')?.textContent ?? ''
    );
    if (!title || !url || Number.isNaN(publishedAt)) return [];
    return [{ id: url, title, url, publishedAt }];
  });
};

export const parseChangelogEntryPage = (
  html: string,
  pageUrl: string
): ChangelogEntryPage => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const imageSrc = doc
    .querySelector('[data-changelog-media-image]')
    ?.getAttribute('src');
  const firstParagraph = doc.querySelector(
    '[data-changelog-prose] p'
  )?.textContent;
  const prose = doc.querySelector('[data-changelog-prose]');
  return {
    imageUrl: imageSrc ? toDocsUrl(imageSrc, pageUrl) : null,
    description: firstParagraph?.replace(/\s+/g, ' ').trim() || null,
    contentHtml: prose ? toContentHtml(prose, pageUrl) : null,
  };
};
